/*
 * Drives the subscription gate tooltip shown when a reader cannot play audio.
 *
 * DnTooltip is remounted through `tooltipKey` on every open so Popper
 * re-measures its position, and the close button is matched by selector
 * because the capturing pointerdown listener fires before its own click
 * handler would.
 */
export default function useAudioLoginTooltip (closeSelector: string) {
  const tooltipRef = ref<any>(null);
  const isOpen = ref(false);
  const tooltipKey = ref(0);
  const outsideClickHandler = ref<((event: PointerEvent) => void) | null>(null);

  const getTooltipPopup = () => {
    const root = tooltipRef.value?.$el as HTMLElement | undefined;
    return root?.querySelector('.tooltip') as HTMLElement | null;
  };

  const unbindOutsideClickHandler = () => {
    if (typeof document === 'undefined' || !outsideClickHandler.value) {
      return;
    }
    document.removeEventListener('pointerdown', outsideClickHandler.value, true);
    outsideClickHandler.value = null;
  };

  const hide = () => {
    unbindOutsideClickHandler();
    tooltipRef.value?.hideTooltip?.();
    isOpen.value = false;
  };

  const bindOutsideClickHandler = () => {
    if (typeof document === 'undefined' || outsideClickHandler.value) {
      return;
    }
    outsideClickHandler.value = (event: PointerEvent) => {
      const tooltipPopup = getTooltipPopup();
      const target = event.target as Node | null;

      if (target instanceof Element && target.closest(closeSelector)) {
        hide();
        return;
      }
      if (tooltipPopup && target && tooltipPopup.contains(target)) {
        return;
      }
      hide();
    };
    document.addEventListener('pointerdown', outsideClickHandler.value, true);
  };

  const show = async () => {
    isOpen.value = false;
    unbindOutsideClickHandler();
    await nextTick();
    tooltipKey.value += 1;
    isOpen.value = true;
    await nextTick();
    bindOutsideClickHandler();
    tooltipRef.value?.showTooltip?.();
  };

  onUnmounted(unbindOutsideClickHandler);

  return {
    tooltipRef,
    isOpen,
    tooltipKey,
    show,
    hide
  };
}
